import { makeAutoObservable, runInAction } from "mobx";

import http from "../../services/http";
import env from "../../config/env";
import { MessageResultState, ServiceResult } from "../../types/general-types";
import {
  activateAccount,
  activateRootAccount,
  getAccounts,
  getActiveSessionKey,
  getRootAccount,
  purgeAllSessions,
  removeAccount,
  upsertAccount,
} from "../../utils/SsoSession";
import {
  UserSessionEndResponseDto,
  UserSessionItemDto,
  UserSessionListDto,
} from "./types";

const { API_BASE_URL } = env;

class ProfileSwitcherStore {
  static readonly id: string = "ProfileSwitcherStore";

  sessions: UserSessionItemDto[] = [];
  isSwitchAvailable: boolean = false;
  isLoaded: boolean = false;
  isBusy: boolean = false;
  loadedFor: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get api() {
    http.defaults.baseURL = API_BASE_URL;
    return http;
  }

  get current(): UserSessionItemDto | undefined {
    return this.sessions.find((s) => s.isCurrent);
  }

  get others(): UserSessionItemDto[] {
    return this.sessions.filter((s) => !s.isCurrent);
  }

  async load() {
    const accounts = getAccounts();
    this.loadedFor = getActiveSessionKey();

    if (accounts.length <= 1) {
      runInAction(() => {
        this.sessions = [];
        this.isSwitchAvailable = false;
        this.isLoaded = true;
      });
      return;
    }

    try {
      const response: ServiceResult<UserSessionListDto> = (
        await this.api.post("/user-session/list", {
          rootSessionKey: getRootAccount()?.sessionKey || null,
          sessionKeys: accounts.map((a) => a.sessionKey),
        })
      ).data;

      if (response?.state === MessageResultState.SUCCESS && response.result) {
        const items = response.result.items || [];
        runInAction(() => {
          this.sessions = items;
          this.isSwitchAvailable = !!response.result.isSwitchAvailable;
          this.isLoaded = true;
        });
        this.syncLocalAccounts(items);
      } else {
        this.handleLoadFailure();
      }
    } catch {
      this.handleLoadFailure();
    }
  }

  handleLoadFailure() {
    const root = getRootAccount();
    const active = getActiveSessionKey();

    if (root && active && active !== root.sessionKey) {
      activateRootAccount();
      window.location.reload();
      return;
    }

    runInAction(() => {
      this.sessions = [];
      this.isSwitchAvailable = false;
      this.isLoaded = true;
    });
  }

  syncLocalAccounts(items: UserSessionItemDto[]) {
    const live = new Set(items.map((i) => i.sessionKey));

    getAccounts()
      .filter((a) => !a.isRoot && !live.has(a.sessionKey))
      .forEach((a) => removeAccount(a.sessionKey));

    items.forEach((i) =>
      upsertAccount({
        sessionKey: i.sessionKey,
        name: i.name,
        userName: i.userName,
        email: i.email,
        personnelId: i.personnelId,
        isRoot: i.isRoot,
      })
    );
  }

  switchTo(session: UserSessionItemDto) {
    if (this.isBusy || session.isCurrent) return;
    runInAction(() => {
      this.isBusy = true;
    });

    activateAccount(session.sessionKey);
    window.location.href = window.location.origin + "/portal-selection";
  }

  async end(session: UserSessionItemDto) {
    if (this.isBusy || session.isRoot) return false;
    runInAction(() => {
      this.isBusy = true;
    });

    try {
      const response: ServiceResult<UserSessionEndResponseDto> = (
        await this.api.post("/user-session/end", {
          sessionKey: session.sessionKey,
          rootSessionKey: getRootAccount()?.sessionKey || null,
        })
      ).data;

      if (response?.state !== MessageResultState.SUCCESS || !response.result) {
        runInAction(() => {
          this.isBusy = false;
        });
        return false;
      }

      removeAccount(session.sessionKey);

      runInAction(() => {
        this.sessions = this.sessions.filter(
          (s) => s.sessionKey !== session.sessionKey
        );
        this.isSwitchAvailable = this.sessions.length > 1;
      });

      if (response.result.shouldReturnToRoot) {
        this.returnToRoot();
        return true;
      }

      runInAction(() => {
        this.isBusy = false;
      });
      return true;
    } catch {
      runInAction(() => {
        this.isBusy = false;
      });
      return false;
    }
  }

  async endOthers() {
    if (this.isBusy) return false;
    runInAction(() => {
      this.isBusy = true;
    });

    try {
      const response: ServiceResult<UserSessionEndResponseDto> = (
        await this.api.post("/user-session/end-others", {
          rootSessionKey: getRootAccount()?.sessionKey || null,
          sessionKeys: getAccounts().map((a) => a.sessionKey),
        })
      ).data;

      if (response?.state !== MessageResultState.SUCCESS || !response.result) {
        runInAction(() => {
          this.isBusy = false;
        });
        return false;
      }

      getAccounts()
        .filter((a) => !a.isRoot)
        .forEach((a) => removeAccount(a.sessionKey));

      runInAction(() => {
        this.sessions = this.sessions.filter((s) => s.isRoot);
        this.isSwitchAvailable = false;
      });

      this.returnToRoot();
      return true;
    } catch {
      runInAction(() => {
        this.isBusy = false;
      });
      return false;
    }
  }

  returnToRoot() {
    activateRootAccount();
    window.location.href = window.location.origin + "/portal-selection";
  }

  async logout() {
    if (this.isBusy) return;
    runInAction(() => {
      this.isBusy = true;
    });

    const accounts = getAccounts();

    if (accounts.length > 1) {
      try {
        await this.api.post("/user-session/end-all", {
          rootSessionKey: getRootAccount()?.sessionKey || null,
          sessionKeys: accounts.map((a) => a.sessionKey),
        });
      } catch {
        purgeAllSessions();
      }
    }

    purgeAllSessions();
    window.location.href = window.location.origin + "/login";
  }
}

export default ProfileSwitcherStore;
